"use client";

import { ProcessedCourse } from "@/common/course/types";
import { CourseRun } from "@/common/courseRun/types";
import useCourseEnrollment from "@/components/CourseContent/ContentRefresh/CourseEnroll/hooks/useCourseEnrollment";
import { useEnrollmentUrl } from "@/components/CourseContent/ContentRefresh/CourseEnroll/hooks/useEnrollmentUrl";

const STUB_COURSE_RUN = {
  key: "",
  seats: [],
  isEnrollable: false,
} as unknown as CourseRun;

/**
 * Returns the enrollment auth URL for the current-course CTA in the compare tool,
 * or `null` when the CTA should be hidden.
 *
 * Returns `null` when:
 * - The course has no active run (no enrollment target).
 * - The course is archived.
 * - The active course run is not enrollable.
 *
 * When a URL is returned, it always routes through Choose Your Track (CYT /
 * track-select), never straight to checkout. This mirrors `EnrollLink`'s
 * `isHeroCta` gate, which restricts `straightToCheckout` to the hero/side-rail
 * CTA only. Routing through CYT ensures 2U enrollment fires and the downstream
 * "enrollment submitted" Cohesion conversion event is triggered.
 */
export function useCompareCtaUrl(course: ProcessedCourse): string | null {
  const courseRun = course.activeCourseRun;
  const safeCourseRun = courseRun ?? STUB_COURSE_RUN;

  const {
    isLoggedIn,
    isCourseEnrollable,
    isCourseArchived,
    commerceCoordinatorUrl,
    paidLeadValues,
    emailOptIn,
  } = useCourseEnrollment({ courseRun: safeCourseRun, course });

  const { authUrl } = useEnrollmentUrl({
    courseRun: safeCourseRun,
    course,
    emailOptIn,
    commerceCoordinatorUrl,
    linkDirectToPaymentPage: paidLeadValues?.linkDirectToPaymentPage,
    isLoggedIn,
    straightToCheckout: false,
    isCourseEnrollable: isCourseEnrollable ?? false,
    isCourseArchived: isCourseArchived ?? false,
  });

  if (!courseRun || isCourseArchived || !isCourseEnrollable) {
    return null;
  }

  return authUrl;
}

export default useCompareCtaUrl;
